import { ThemeProvider } from '@emotion/react';
import { CssBaseline } from '@mui/material';
import { CategoryScale } from 'chart.js';
import Chart from 'chart.js/auto';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Route, Routes } from 'react-router-dom';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import SubscriptionPaymentProtectedRoute from './components/Auth/SubscriptionPaymentProtectedRoute';
import AddNewJobPostForm from './components/Company/AddNewJobPostForm/AddNewJobPostForm';
import Analytics from './components/Company/Analytics/Analytics';
import { UserRoleEnum } from './enums/user-data-field-enum';
import BaseLayout from './layouts/BaseLayout';
import BlankLayout from './layouts/BlankLayout';
import AccountSettingsPage from './pages/AccountSettingPage/AccountSettingsPage';
import CompanyPage from './pages/auth/CompanyPage/CompanyPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage/ForgotPasswordPage';
import GetStartedPage from './pages/auth/GetStartedPage/GetStartedPage';
import InvitationsPage from './pages/auth/InvitationsPage/InvitationsPage';
import LoginPage from './pages/auth/LoginPage/LoginPage';
import ProfilePage from './pages/auth/ProfilePage/ProfilePage';
import RegistrationPage from './pages/auth/RegistrationPage/RegistrationPage';
import UpdatePasswordPage from './pages/auth/UpdatePasswordPage/UpdatePasswordPage';
import VerifyAccountPage from './pages/auth/VerifyAccountPage/VerifyAccountPage';
import VerifyCodePage from './pages/auth/VerifyCodePage/VerifyCodePage';
import WelcomeToPlatformPage from './pages/auth/WelcomeToPlatformPage/WelcomeToPlatformPage';
import CandidatesPage from './pages/CandidatesPage/CandidatesPage';
import CareerDevelopmentPage from './pages/career/CareerDevelopmentPage/CareerDevelopmentPage';
import CareerQuestionnairePage from './pages/career/CareerQuestionnairePage';
import LiveAgentInProgressPage from './pages/career/LiveAgentInProgressPage/LiveAgentInProgressPage';
import PersonalizedCareerModalPage from './pages/career/PersonalizedCareerModalPage';
import ProgressPage from './pages/career/ProgressPage/ProgressPage';
import QuizResultsPage from './pages/career/QuizResultsPage/QuizResultsPage';
import SetCareerDevelopmentMapPage from './pages/career/SetCareerDevelopmentMapPage/SetCareerDevelopmentMapPage';
import ChatPage from './pages/ChatPage/ChatPage';
import GlobalSearchPage from './pages/ClobalSearchPage/GlobalSearchPage';
import CompanyRegistrationPage from './pages/company/CompanyRegistrationPage/CompanyRegistrationPage';
import CompanyQuestionnairePage from './pages/CompanyQuestionnairePage/CompanyQuestionnairePage';
import ConnectionsPage from './pages/ConnectionsPage/ConnectionsPage';
import CompanyInfoPage from './pages/home/CompanyInfoPage/CompanyInfoPage';
import MyFeedPage from './pages/home/MyFeedPage/MyFeedPage';
import MyJobApplicationDetailsPage from './pages/home/MyJobApplicationDetailsPage/MyJobApplicationDetailsPage';
import PersonalInfoPage from './pages/home/PersonalInfoPage/PersonalInfoPage';
import JobPostApplyPage from './pages/JobPostApplyPage/JobPostApplyPage';
import JobPostsPage from './pages/JobPostsPage/JobPostsPage';
import ProfileSettingsPage from './pages/live-agent/ProfileSettingsPage/ProfileSettingsPage';
import MyJobPostPage from './pages/MyJobPostPage/MyJobPostPage';
import SubscriptionPaymentPage from './pages/subscription/SubscriptionPaymentPage';
import SubscriptionPage from './pages/subscription/SubscriptionPlansPage';
import SubscriptionSuccessfullyPage from './pages/SubscriptionSuccessfullyPage/SubscriptionSuccessfullyPage';
import { selectAuthData } from './store/selectors/authSelector';
import { useThemeContext } from './theme/ThemeContextProvider';
import ViewPostPage from './pages/home/ViewPostPage/ViewPostPage';
import VerifiedProfileRoute from './components/Auth/VerifiedProfileRoute';

Chart.register(CategoryScale);

const App = () => {
  const { theme } = useThemeContext();
  const authData = useSelector(selectAuthData);
  const role = authData?.user?.role;

  const pagesForDefaultPath = useMemo(() => {
    switch (role) {
      case UserRoleEnum.COMPANY:
        return <CompanyInfoPage />;
      case UserRoleEnum.USER:
        return <MyFeedPage />;
      case UserRoleEnum.AGENT:
        return <LiveAgentInProgressPage />;
      default:
        return null;
    }
  }, [role]);

  const pagesForMePath = useMemo(() => {
    switch (role) {
      case UserRoleEnum.COMPANY:
        return <CompanyInfoPage />;
      case UserRoleEnum.USER:
        return <PersonalInfoPage />;
      case UserRoleEnum.AGENT:
        return <LiveAgentInProgressPage />;
      default:
        return null;
    }
  }, [role]);

  const pagesForProfilePath = useMemo(() => {
    switch (role) {
      case UserRoleEnum.COMPANY:
        return <CompanyPage />;
      case UserRoleEnum.USER:
        return <ProfilePage />;
      case UserRoleEnum.AGENT:
        return <LiveAgentInProgressPage />;
      default:
        return null;
    }
  }, [role]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <div className="App">
        <Routes>
          {/* Public routes */}
          <Route>
            <Route path="login" element={<LoginPage />} />

            <Route element={<BlankLayout />}>
              <Route path="registration" element={<RegistrationPage />} />
              <Route path="verify-code" element={<VerifyCodePage />} />
              <Route path="forgot-password" element={<ForgotPasswordPage />} />
              <Route path="update-password" element={<UpdatePasswordPage />} />
            </Route>
            <Route
              path="registration/company"
              element={<CompanyRegistrationPage />}
            />
          </Route>

          {/* Protected routes */}
          <Route element={<ProtectedRoute />}>
            <Route
              path="profile"
              element={<BaseLayout>{pagesForProfilePath}</BaseLayout>}
            />
            <Route
              path="welcome"
              element={<WelcomeToPlatformPage step={1} />}
            />
            <Route
              path="welcome-to-platform"
              element={<WelcomeToPlatformPage step={2} />}
            />
            <Route path="verify-account" element={<VerifyAccountPage />} />
            <Route path="get-started" element={<GetStartedPage />} />

            {/* Verified Profile routes */}
            <Route element={<VerifiedProfileRoute />}>
              <Route path="/" element={<BaseLayout />}>
                <Route path="" element={pagesForDefaultPath} />
                <Route path="me" element={pagesForMePath}>
                  <Route
                    path="job-application/:applicationId"
                    element={<MyJobApplicationDetailsPage />}
                  />
                </Route>
                <Route path="candidates" element={<CandidatesPage />} />
                <Route path=":id" element={<PersonalInfoPage />} />
                {/* feature for future */}
                <Route
                  path="account-settings"
                  element={<AccountSettingsPage />}
                />
                <Route path="connections" element={<ConnectionsPage />} />
                <Route path="global-search" element={<GlobalSearchPage />} />
                <Route
                  path="company-questionnaire"
                  element={<CompanyQuestionnairePage />}
                />
                <Route
                  path="subscription-plans"
                  element={
                    <SubscriptionPaymentProtectedRoute>
                      <SubscriptionPage />
                    </SubscriptionPaymentProtectedRoute>
                  }
                />
                <Route
                  path="subscription-payment"
                  element={
                    <SubscriptionPaymentProtectedRoute>
                      <SubscriptionPaymentPage />
                    </SubscriptionPaymentProtectedRoute>
                  }
                />
                <Route
                  path="subscription-successfully-paid"
                  element={
                    <SubscriptionPaymentProtectedRoute>
                      <SubscriptionSuccessfullyPage />
                    </SubscriptionPaymentProtectedRoute>
                  }
                />
                <Route
                  path="personalized-career-modal"
                  element={<PersonalizedCareerModalPage />}
                />
                <Route
                  path="career-questionnaire"
                  element={<CareerQuestionnairePage />}
                />
                <Route
                  path="career-development"
                  element={<CareerDevelopmentPage />}
                />
                <Route
                  path="live-agent"
                  element={<LiveAgentInProgressPage />}
                />
                <Route
                  path="set-career-development-map"
                  element={<SetCareerDevelopmentMapPage />}
                />
                <Route path="progress" element={<ProgressPage />} />
                <Route path="quiz-results" element={<QuizResultsPage />} />
                <Route
                  path="profile-settings"
                  element={<ProfileSettingsPage />}
                />
                <Route path="invitations" element={<InvitationsPage />} />
                <Route path="chat" element={<ChatPage />} />
                <Route path="job-posts" element={<JobPostsPage />} />
                <Route
                  path="job-post-apply/:id"
                  element={<JobPostApplyPage />}
                />
                <Route path="my-job-post/:id" element={<MyJobPostPage />} />
                <Route
                  path="manage-job-post/:id"
                  element={<AddNewJobPostForm />}
                />
                <Route path="post/:id" element={<ViewPostPage />} />
                <Route path="analytics" element={<Analytics />} />
              </Route>
            </Route>
          </Route>
        </Routes>
      </div>
    </ThemeProvider>
  );
};

export default App;
